import * as React from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  flexRender,
  createColumnHelper,
  SortingState,
  ColumnFiltersState,
} from '@tanstack/react-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Eye,
  Sparkles,
  AlertCircle,
  Search,
} from 'lucide-react'
import { AssetWithClassification, isNewAsset, getAssetClassTheme, getAssetClassLabel } from '@/types/assets'
import { ClassificationToggle } from './ClassificationToggle'
import { AssetClass } from '@/types/api'
import { cn } from '@/lib/utils'

interface AssetClassificationTableProps {
  /** Array of assets to display */
  assets: AssetWithClassification[]
  /** Loading state */
  isLoading?: boolean
  /** Callback when classification changes */
  onClassificationChange: (
    assetId: string,
    classification: AssetClass,
    isOverride: boolean
  ) => void
  /** Callback when user resets to default */
  onResetToDefault: (assetId: string) => void
  /** Callback to view asset details */
  onViewDetails: (asset: AssetWithClassification) => void
  /** Search query */
  searchQuery?: string
  /** Error state */
  error?: Error | null
}

const columnHelper = createColumnHelper<AssetWithClassification>()

/**
 * AssetClassificationTable Component
 *
 * A comprehensive table for viewing and managing asset classifications.
 * Features:
 * - Sortable columns (ticker, name, classification)
 * - Inline classification toggle with override indication
 * - New asset highlighting (within 30 days)
 * - Override status badges
 * - Loading skeletons
 * - Empty and error states
 * - Row actions for viewing details
 *
 * @example
 * ```tsx
 * <AssetClassificationTable
 *   assets={assets}
 *   isLoading={false}
 *   onClassificationChange={(id, val, override) => console.log(id, val, override)}
 *   onResetToDefault={(id) => console.log('reset', id)}
 *   onViewDetails={(asset) => console.log('view', asset)}
 *   filter="all"
 *   searchQuery=""
 * />
 * ```
 */
export function AssetClassificationTable({
  assets,
  isLoading = false,
  onClassificationChange,
  onResetToDefault,
  onViewDetails,
  searchQuery = '',
  error = null,
}: AssetClassificationTableProps) {
  const [sorting, setSorting] = React.useState<SortingState>([
    { id: 'symbol', desc: false },
  ])
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([])

  // Define columns
  const columns = React.useMemo(
    () => [
      columnHelper.accessor('symbol', {
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8 font-semibold"
          >
            Ticker
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        ),
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <span className="font-mono font-semibold">{row.original.symbol}</span>
            {isNewAsset(row.original.metadata?.listingDate || row.original.listingDate) && (
              <Badge
                variant="outline"
                className="bg-blue-50 text-blue-700 border-blue-200 text-xs"
              >
                <Sparkles className="w-3 h-3 mr-1" />
                New
              </Badge>
            )}
          </div>
        ),
      }),

      columnHelper.accessor('name', {
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8 font-semibold"
          >
            Name
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-gray-700">{row.original.name}</span>
        ),
      }),

      columnHelper.accessor('assetClass', {
        header: () => <span className="font-semibold">Classification</span>,
        cell: ({ row }) => (
          <ClassificationToggle
            value={row.original.assetClass}
            isOverride={row.original.isOverride}
            rulesBasedClass={row.original.rulesBasedClass}
            onChange={(value, isOverride) =>
              onClassificationChange(row.original.id, value, isOverride)
            }
            onReset={() => onResetToDefault(row.original.id)}
            hasPositions={(row.original.positionCount || 0) > 0}
            size="sm"
          />
        ),
      }),

      columnHelper.accessor('rulesBasedClass', {
        header: () => (
          <span className="font-semibold text-muted-foreground">Default</span>
        ),
        cell: ({ row }) => {
          const theme = getAssetClassTheme(row.original.rulesBasedClass)
          return (
            <Badge
              variant="secondary"
              className={cn(
                'text-xs font-normal',
                theme.badge
              )}
            >
              {getAssetClassLabel(row.original.rulesBasedClass)}
            </Badge>
          )
        },
      }),

      columnHelper.accessor('isOverride', {
        header: () => (
          <span className="font-semibold text-muted-foreground">Status</span>
        ),
        cell: ({ row }) => {
          if (!row.original.isOverride) {
            return (
              <span className="text-sm text-gray-400">Default</span>
            )
          }
          return (
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-200 text-xs"
            >
              <AlertCircle className="w-3 h-3 mr-1" />
              Modified
            </Badge>
          )
        },
      }),

      columnHelper.accessor('positionCount', {
        header: ({ column }) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-8 font-semibold"
          >
            Positions
            {column.getIsSorted() === 'asc' ? (
              <ArrowUp className="ml-2 h-4 w-4" />
            ) : column.getIsSorted() === 'desc' ? (
              <ArrowDown className="ml-2 h-4 w-4" />
            ) : (
              <ArrowUpDown className="ml-2 h-4 w-4" />
            )}
          </Button>
        ),
        cell: ({ row }) => (
          <span className="text-sm text-gray-600">
            {row.original.positionCount || 0}
          </span>
        ),
      }),

      columnHelper.display({
        id: 'actions',
        header: () => <span className="sr-only">Actions</span>,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0"
            onClick={(e) => {
              e.stopPropagation()
              onViewDetails(row.original)
            }}
            aria-label="View asset details"
          >
            <Eye className="h-4 w-4" />
          </Button>
        ),
      }),
    ],
    [onClassificationChange, onResetToDefault, onViewDetails]
  )

  // Apply search filter
  const filteredAssets = React.useMemo(() => {
    let result = assets

    // Apply search query
    if (searchQuery.trim()) {
      const term = searchQuery.toLowerCase().trim()
      result = result.filter(
        (asset) =>
          asset.symbol.toLowerCase().includes(term) ||
          asset.name.toLowerCase().includes(term)
      )
    }

    return result
  }, [assets, searchQuery])

  const table = useReactTable({
    data: filteredAssets,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
  })

  // Loading state
  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-6 text-center">
        <AlertCircle className="mx-auto h-8 w-8 text-red-500 mb-2" />
        <h3 className="text-lg font-medium text-red-800">Failed to load assets</h3>
        <p className="text-sm text-red-600 mt-1">{error.message}</p>
      </div>
    )
  }

  // Empty state
  if (filteredAssets.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
        <Search className="mx-auto h-8 w-8 text-gray-400 mb-2" />
        <h3 className="text-lg font-medium text-gray-700">No assets found</h3>
        <p className="text-sm text-gray-500 mt-1">
          {searchQuery
            ? `No assets match "${searchQuery}"`
            : 'Try adjusting your filters to see more results.'}
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext()
                      )}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
                className={cn(
                  'cursor-pointer hover:bg-gray-50',
                  row.original.isOverride && 'bg-amber-50/50'
                )}
                onClick={() => onViewDetails(row.original)}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No assets found.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
